/**
 * roster — the Club tab's day roster (build 15, spec 2026-09-14 "Landing: DAY ROSTER").
 *
 * "I already know my swim. I want to see my clubmate's swims." (Scott.) The tab lands on the
 * date row's day as a list: ONE ROW PER SWIM (reactions target a swim, so a swimmer with two
 * swims has two rows, each carrying its time of day). Nothing opens by itself.
 *
 * ORDER = clubmates' swims by start time, then the viewer's swims, then member id (then swim
 * id, so a re-render never reshuffles). Never distance: that ranks.
 *
 * SINGLE-SWIMMER DAY: one line per OTHER member naming their most recent stored swim BEFORE the
 * day ("Ryan last swam Sat 12"). It links only when the strip can show that day. Never "nobody
 * swam": a member with no stored swim before the day gets no line.
 */
import type { ClubMember, ClubSnapshot, ClubSwim } from "./snapshot";
import type { Lap } from "../laps";
import { nameOf, rowCaption, startOf, type SwimSets } from "./swimCard";
import { dayKeyDate, localDayKey } from "./strip";
import { fmtMeters, fmtTimeOfDay, formatDuration } from "../format";

export interface RosterRow {
  member: ClubMember;
  swim: ClubSwim;
  /** "You" on the viewer's rows. */
  name: string;
  isSelf: boolean;
  /** "7:00 AM", only when this swimmer has 2+ swims that day. */
  timeOfDay: string | null;
  /** "2,450 m · 1h 1m · 25 m pool". */
  line: string;
  sets: SwimSets;
  /** Every length of the swim, reindexed 1..n (SetGroupRibbon keys chips by index). */
  laps: Lap[];
  caption: string;
  /** False for an own swim the club never received (B-028 session-only): no reaction row. */
  reactable: boolean;
}

export interface LastSwamLine {
  member: ClubMember;
  name: string;
  /** The day of their most recent swim before the shown day. */
  dayKey: string;
  /** The date row can reach that day, so the line moves it there (the strip follows, any week). */
  linked: boolean;
  /** "Ryan last swam Tue 8" (a recent day) or "Ryan last swam Aug 2". The view adds " ›" to a link. */
  text: string;
}

export interface LastSwamOptions {
  /** The date row has a session on that day. */
  linkable: (dayKey: string) => boolean;
  /** The day is inside the strip's recent weeks, so "Tue 8" reads unambiguously; else "Aug 8". */
  recent: (dayKey: string) => boolean;
}

const dayOf = (swim: ClubSwim): string => localDayKey(Date.parse(swim.date));

function rowLine(swim: ClubSwim): string {
  const pool = (swim.poolM ?? 0) > 0 ? ` · ${fmtMeters(swim.poolM)} pool` : "";
  return `${fmtMeters(swim.distanceM)} · ${formatDuration(swim.durationSec)}${pool}`;
}

/** VP round 2: on an estimate the tally lists strokes only; the unresolved lengths ("?" chip word) are
 *  counted in the status line instead (swimCard.sourceLine). */
function estimateCaption(caption: string): string {
  return caption
    .split(" · ")
    .filter((part) => !part.startsWith("? "))
    .join(" · ");
}

export function rosterRows(
  snapshot: ClubSnapshot,
  dayKey: string,
  setsFor: (member: ClubMember, swim: ClubSwim) => SwimSets,
  sessionOnly: ReadonlySet<string> = new Set(),
): RosterRow[] {
  const rows: RosterRow[] = [];
  for (const member of snapshot.members) {
    const swims = member.swims.filter((s) => dayOf(s) === dayKey);
    const isSelf = member.id === snapshot.self;
    for (const swim of swims) {
      const sets = setsFor(member, swim);
      const laps = sets.groups.flatMap((g) => g.laps).map((lap, i) => ({ ...lap, index: i + 1 }));
      rows.push({
        member,
        swim,
        name: nameOf(member, snapshot.self),
        isSelf,
        timeOfDay: swims.length > 1 ? fmtTimeOfDay(startOf(swim)) : null,
        line: rowLine(swim),
        sets,
        laps,
        caption: laps.length === 0 ? "" : sets.source === "summary" ? estimateCaption(rowCaption(laps)) : rowCaption(laps),
        reactable: !sessionOnly.has(swim.id),
      });
    }
  }
  return rows.sort(
    (a, b) =>
      Number(a.isSelf) - Number(b.isSelf) ||
      startOf(a.swim) - startOf(b.swim) ||
      a.member.id.localeCompare(b.member.id) ||
      a.swim.id.localeCompare(b.swim.id),
  );
}

/** "Swam Wed · 3 swims" (the view uppercases it like every eyebrow). */
export function rosterEyebrow(dayKey: string, swimCount: number): string {
  const weekday = dayKeyDate(dayKey).toLocaleDateString(undefined, { weekday: "short" });
  return `Swam ${weekday} · ${swimCount} ${swimCount === 1 ? "swim" : "swims"}`;
}

/** The one member who swam that day, or null (nobody, or 2+). */
function singleSwimmer(snapshot: ClubSnapshot, dayKey: string): ClubMember | null {
  const swimmers = snapshot.members.filter((m) => m.swims.some((s) => dayOf(s) === dayKey));
  return swimmers.length === 1 ? swimmers[0] : null;
}

export function lastSwamLines(snapshot: ClubSnapshot, dayKey: string, opts: LastSwamOptions): LastSwamLine[] {
  const only = singleSwimmer(snapshot, dayKey);
  if (!only) return [];
  const lines: LastSwamLine[] = [];
  for (const member of snapshot.members) {
    if (member.id === only.id) continue;
    let last: string | null = null;
    for (const s of member.swims) {
      const k = dayOf(s);
      if (k < dayKey && (last === null || k > last)) last = k;
    }
    if (last === null) continue;
    const name = nameOf(member, snapshot.self);
    const linked = opts.linkable(last);
    const d = dayKeyDate(last);
    const when = `${d.toLocaleDateString(undefined, opts.recent(last) ? { weekday: "short" } : { month: "short" })} ${d.getDate()}`;
    lines.push({ member, name, dayKey: last, linked, text: `${name} last swam ${when}` });
  }
  const self = (l: LastSwamLine) => Number(l.member.id === snapshot.self);
  return lines.sort((a, b) => self(a) - self(b) || a.name.localeCompare(b.name) || a.member.id.localeCompare(b.member.id));
}

/** VP round 1: on a single-swimmer day, the members with no swim before that day are named in ONE
 *  line ("No swims yet: Ava, Ben, you") instead of silence. Null when there are none. */
export function noSwimsLine(snapshot: ClubSnapshot, dayKey: string): string | null {
  const only = singleSwimmer(snapshot, dayKey);
  if (!only) return null;
  const quiet = snapshot.members.filter((m) => m.id !== only.id && !m.swims.some((s) => dayOf(s) < dayKey));
  if (quiet.length === 0) return null;
  const isSelf = (m: ClubMember) => Number(m.id === snapshot.self);
  const names = quiet
    .sort((a, b) => isSelf(a) - isSelf(b) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .map((m) => (m.id === snapshot.self ? "You" : m.name.trim() || "a clubmate"));
  return `No swims yet: ${names.join(", ")}`;
}
